import React, {
    useRef,
    useEffect,
    useLayoutEffect,
    useState,
    useMemo,
} from "react";
import {
    useCurrentFrame,
    useVideoConfig,
    useDelayRender,
    interpolate,
    useRemotionEnvironment,
} from "remotion";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { point } from "@turf/helpers";
import turfDistance from "@turf/distance";
import { satelliteStyle, TERRAIN_SPEC } from "../../mapbox-style";
import type { TripData, TrackPoint, TripPhoto } from "../../types";
import { VIDEO_WIDTH, VIDEO_HEIGHT, VIDEO_FPS } from "../../types";
import type { SpeedUnit, DistanceUnit } from "../../units";
import { formatDistance, formatSpeed } from "../../units";

export let activeMapInstance: mapboxgl.Map | null = null;
export let activeTrackCamera: TrackCamera | null = null;

const PHOTO_DISPLAY_FRAMES = Math.round(3 * VIDEO_FPS); // how long a photo holds on screen
const PHOTO_FADE_FRAMES = 15; // quick fade in/out at each end of the hold
const PHOTO_LEAD_FRAMES = PHOTO_FADE_FRAMES - 5; // start fading the photo in this many frames before the boat actually stops, so it's already visible by the time it does — avoids a stop-then-wait-then-fade gap
const PAUSE_EASE_FRAMES = 8; // frames spent easing the track's speed down into, and back up out of, a photo hold

// Native pixel size of the generated photo-marker icon; "photo-pins"
// icon-size scales this down at render time.
const PHOTO_MARKER_ICON_SIZE = 128;

function buildPhotoMarkerIconDataUrl(): string {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${PHOTO_MARKER_ICON_SIZE}" height="${PHOTO_MARKER_ICON_SIZE}" viewBox="0 0 128 128">
  <circle cx="64" cy="64" r="58" fill="#38BDF8" stroke="#ffffff" stroke-width="8"/>
  <rect x="34" y="50" width="60" height="40" rx="8" fill="#ffffff"/>
  <rect x="54" y="38" width="20" height="14" rx="3" fill="#ffffff"/>
  <circle cx="64" cy="70" r="13" fill="#38BDF8"/>
  <circle cx="64" cy="70" r="8" fill="#ffffff"/>
</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// Built once at module init — the icon is static, so there's no need to
// regenerate the data URL per map instance or per frame.
const PHOTO_MARKER_ICON_DATA_URL = buildPhotoMarkerIconDataUrl();

interface Props {
    tripData: TripData;
    mapboxToken: string;
    cameraMode: "follow" | "overview";
    terrainEnabled: boolean;
    outroFrames?: number;
    title?: string;
    totalDistanceM?: number;
    avgSpeedMps?: number;
    distanceUnit?: DistanceUnit;
    speedUnit?: SpeedUnit;
    underwayDurationSeconds?: number;
    watermarkImage?: string;
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

function lerpAngle(a: number, b: number, t: number): number {
    let diff = b - a;
    while (diff > 180) diff -= 360;
    while (diff < -180) diff += 360;
    return a + diff * t;
}

function easeInOut(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

function formatDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Derives bearing from track geometry over a window — smoother than raw GPS heading.
function smoothedBearing(
    points: TrackPoint[],
    progress: number,
    windowFrac = 0.04,
): number {
    const ptBehind = pointAtProgress(
        points,
        Math.max(0, progress - windowFrac),
    );
    const ptAhead = pointAtProgress(points, Math.min(1, progress + windowFrac));
    const dLat = ptAhead.lat - ptBehind.lat;
    const dLon = ptAhead.lon - ptBehind.lon;
    return ((Math.atan2(dLon, dLat) * 180) / Math.PI + 360) % 360;
}

function binarySearchTime(points: TrackPoint[], targetTime: number): number {
    let lo = 0,
        hi = points.length - 2;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (points[mid].timestamp <= targetTime) lo = mid;
        else hi = mid - 1;
    }
    return lo;
}

// Returns fractional index in [0, points.length-1] based on cumulative distance,
// so constant-speed mode advances evenly over geographic distance.
function pointProgressFromDistance(
    cumulativeNm: number[],
    progress: number,
): number {
    if (cumulativeNm.length <= 1) return 0;
    const totalDist = cumulativeNm[cumulativeNm.length - 1];
    if (totalDist === 0) return 0;
    const targetDist = progress * totalDist;
    let lo = 0,
        hi = cumulativeNm.length - 2;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (cumulativeNm[mid] <= targetDist) lo = mid;
        else hi = mid - 1;
    }
    const a = cumulativeNm[lo],
        b = cumulativeNm[lo + 1] ?? a;
    const dd = b - a;
    return lo + (dd > 0 ? (targetDist - a) / dd : 0);
}

// Interpolates the point on the track at an arbitrary timestamp (clamped to
// the track's own time range).
function pointAtTimestamp(points: TrackPoint[], timestamp: number): TrackPoint {
    if (points.length === 1) return points[0];
    const targetTime = Math.max(
        points[0].timestamp,
        Math.min(points[points.length - 1].timestamp, timestamp),
    );
    const lo = binarySearchTime(points, targetTime);
    const a = points[lo],
        b = points[lo + 1] ?? points[lo];
    const dt = b.timestamp - a.timestamp;
    const frac = dt > 0 ? (targetTime - a.timestamp) / dt : 0;
    return {
        lat: lerp(a.lat, b.lat, frac),
        lon: lerp(a.lon, b.lon, frac),
        timestamp: targetTime,
        speed:
            a.speed != null && b.speed != null
                ? lerp(a.speed, b.speed, frac)
                : (a.speed ?? b.speed),
        heading:
            a.heading != null && b.heading != null
                ? lerpAngle(a.heading, b.heading, frac)
                : (a.heading ?? b.heading),
    };
}

function pointAtProgress(points: TrackPoint[], progress: number): TrackPoint {
    if (points.length === 1) return points[0];
    const targetTime = lerp(
        points[0].timestamp,
        points[points.length - 1].timestamp,
        progress,
    );
    return pointAtTimestamp(points, targetTime);
}

// Interpolates by fractional array index rather than timestamp (for constant-speed mode).
function pointAtIndex(points: TrackPoint[], fracIndex: number): TrackPoint {
    if (points.length === 1) return points[0];
    const lo = Math.max(0, Math.min(points.length - 2, Math.floor(fracIndex)));
    const frac = Math.min(1, fracIndex - lo);
    const a = points[lo];
    const b = points[lo + 1] ?? points[lo];
    return {
        lat: lerp(a.lat, b.lat, frac),
        lon: lerp(a.lon, b.lon, frac),
        timestamp: lerp(a.timestamp, b.timestamp, frac),
        speed:
            a.speed != null && b.speed != null
                ? lerp(a.speed, b.speed, frac)
                : (a.speed ?? b.speed),
        heading:
            a.heading != null && b.heading != null
                ? lerpAngle(a.heading, b.heading, frac)
                : (a.heading ?? b.heading),
    };
}

function smoothedBearingByIndex(
    points: TrackPoint[],
    fracIndex: number,
    window = 2,
): number {
    const ptBehind = pointAtIndex(points, Math.max(0, fracIndex - window));
    const ptAhead = pointAtIndex(
        points,
        Math.min(points.length - 1, fracIndex + window),
    );
    const dLat = ptAhead.lat - ptBehind.lat;
    const dLon = ptAhead.lon - ptBehind.lon;
    return ((Math.atan2(dLon, dLat) * 180) / Math.PI + 360) % 360;
}

// Locates a photo's capture time on the track by the *same* time-based
// interpolation as the pin marker, then converts that position to a
// distance-based progress fraction (0-1) — the coordinate space
// constant-speed playback actually moves through. Matching in this space
// (rather than approximating via interpolated timestamps) guarantees the
// boat lands exactly on the photo's location once its progress is reached.
function photoTargetProgress(
    points: TrackPoint[],
    cumulativeNm: number[],
    timestamp: number,
): { lat: number; lon: number; progress: number } {
    if (points.length === 1) {
        return { lat: points[0].lat, lon: points[0].lon, progress: 0 };
    }
    const targetTime = Math.max(
        points[0].timestamp,
        Math.min(points[points.length - 1].timestamp, timestamp),
    );
    const lo = binarySearchTime(points, targetTime);
    const a = points[lo],
        b = points[lo + 1] ?? points[lo];
    const dt = b.timestamp - a.timestamp;
    const frac = dt > 0 ? (targetTime - a.timestamp) / dt : 0;
    const distA = cumulativeNm[lo] ?? 0;
    const distB = cumulativeNm[lo + 1] ?? distA;
    const totalDist = cumulativeNm[cumulativeNm.length - 1] || 0;
    const progress =
        totalDist > 0 ? (distA + frac * (distB - distA)) / totalDist : 0;
    return {
        lat: lerp(a.lat, b.lat, frac),
        lon: lerp(a.lon, b.lon, frac),
        progress,
    };
}

// Inverts constantProgress = lerp(t, easeInOut(t), 0.4) by bisection — that
// blend is monotonic in t but has no closed-form inverse.
function inverseConstantProgress(target: number): number {
    let lo = 0,
        hi = 1;
    for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        const v = lerp(mid, easeInOut(mid), 0.4);
        if (v < target) lo = mid;
        else hi = mid;
    }
    return (lo + hi) / 2;
}

interface PhotoCue {
    dataUrl: string;
    triggerFrame: number;
    lat: number;
    lon: number;
}

// Exact discrete deficit (in frames) that carving one easeInOut ramp of
// PAUSE_EASE_FRAMES frames into an otherwise rate=1 region removes from the
// cumulative sum — used both to size the total slowdown up front and to
// pre-compensate a cue's own trigger-frame search (see below).
let PAUSE_RAMP_DEFICIT = 0;
for (let k = 0; k < PAUSE_EASE_FRAMES; k++) {
    PAUSE_RAMP_DEFICIT += easeInOut(k / PAUSE_EASE_FRAMES);
}

// Solves photo cue placement and the paused frame map together, in a single
// forward pass over photos sorted by capture time.
//
// Each photo's target is a distance-based track progress (see
// photoTargetProgress) — the exact coordinate space constant-speed playback
// moves through — so once its trigger frame is found, the boat is
// necessarily at that exact location, matching its pin marker precisely.
//
// The total time every pause+ease costs is a fixed, position-independent
// number of frames, so the one global speed-up needed to repay all of them
// out of the fixed frame budget is solved analytically up front rather than
// iteratively: an earlier iterative version (rebuild cues against a pause
// map, rebuild the pause map from those cues, repeat) could oscillate
// forever, because a cue's own pause — which starts a few frames *before*
// its own trigger frame — retroactively changed the very calculation used
// to place it.
//
// Cues are instead placed one at a time in chronological order, each
// solved only against the pauses already carved in by earlier cues (never
// its own), with a one-time correction (PAUSE_RAMP_DEFICIT) added to the
// search target to pre-account for the deceleration this cue is about to
// carve immediately before its own trigger frame.
function computePausedPhotoSchedule(
    photos: TripPhoto[],
    mapPhaseFrames: number,
    points: TrackPoint[],
    cumulativeNm: number[],
): { photoCues: PhotoCue[]; pausedFrameMap: Float32Array } {
    if (mapPhaseFrames <= 0 || points.length === 0) {
        return {
            photoCues: [],
            pausedFrameMap: new Float32Array(Math.max(mapPhaseFrames, 0)),
        };
    }

    const firstTime = points[0].timestamp;
    const lastTime = points[points.length - 1].timestamp;
    const validPhotos = [...photos]
        .filter((p) => p.timestamp >= firstTime && p.timestamp <= lastTime)
        .sort((a, b) => a.timestamp - b.timestamp);

    const perCueDeficit = PHOTO_DISPLAY_FRAMES + PAUSE_EASE_FRAMES;
    const maxNominal = Math.max(
        1,
        mapPhaseFrames - 1 - validPhotos.length * perCueDeficit,
    );
    const scale = (mapPhaseFrames - 1) / maxNominal;

    const rate = new Float32Array(mapPhaseFrames).fill(1);
    const rawNominal = new Float32Array(mapPhaseFrames);
    const reintegrateFrom = (fromFrame: number, startAcc: number) => {
        let acc = startAcc;
        for (let f = fromFrame; f < mapPhaseFrames; f++) {
            rawNominal[f] = acc;
            acc += rate[f];
        }
    };
    reintegrateFrom(0, 0);

    const photoCues: PhotoCue[] = [];
    for (const photo of validPhotos) {
        const { lat, lon, progress } = photoTargetProgress(
            points,
            cumulativeNm,
            photo.timestamp,
        );
        const trackProgress = inverseConstantProgress(progress);
        const targetRaw = (trackProgress * (mapPhaseFrames - 1)) / scale;
        const searchTarget = targetRaw + PAUSE_RAMP_DEFICIT;

        let lo = 0,
            hi = mapPhaseFrames - 1;
        while (lo < hi) {
            const mid = (lo + hi) >> 1;
            if (rawNominal[mid] < searchTarget) lo = mid + 1;
            else hi = mid;
        }
        const naturalFrame = lo;

        const previous = photoCues[photoCues.length - 1];
        const earliestStart = previous
            ? previous.triggerFrame +
              PHOTO_DISPLAY_FRAMES +
              2 * PHOTO_LEAD_FRAMES
            : 0;
        const triggerFrame = Math.max(naturalFrame, earliestStart);
        if (triggerFrame > mapPhaseFrames - 1) continue;
        photoCues.push({ dataUrl: photo.dataUrl, triggerFrame, lat, lon });

        const holdStart = triggerFrame;
        const holdEnd = triggerFrame + PHOTO_DISPLAY_FRAMES;
        const decelStart = holdStart - PAUSE_EASE_FRAMES;
        const accelEnd = holdEnd + PAUSE_EASE_FRAMES;
        const from = Math.max(0, decelStart);
        for (let f = from; f < Math.min(mapPhaseFrames, accelEnd); f++) {
            let r = 1;
            if (f >= holdStart && f < holdEnd) {
                r = 0;
            } else if (f < holdStart) {
                r = 1 - easeInOut((f - decelStart) / PAUSE_EASE_FRAMES);
            } else {
                r = easeInOut((f - holdEnd) / PAUSE_EASE_FRAMES);
            }
            rate[f] = Math.min(rate[f], r);
        }
        reintegrateFrom(from, rawNominal[from]);
    }

    const pausedFrameMap = new Float32Array(mapPhaseFrames);
    for (let f = 0; f < mapPhaseFrames; f++) {
        pausedFrameMap[f] = rawNominal[f] * scale;
    }
    return { photoCues, pausedFrameMap };
}

interface TrackCamera {
    zoom: number;
    center: [number, number];
}

// Approximates cameraForBounds without a Map instance so the constructor can
// start at (roughly) the right position instead of a generic zoom-13 view.
function estimateInitialCamera(
    points: TrackPoint[],
    cameraMode: "follow" | "overview",
): { center: [number, number]; zoom: number; pitch: number; bearing: number } {
    const lons = points.map((p) => p.lon);
    const lats = points.map((p) => p.lat);
    const minLon = Math.min(...lons),
        maxLon = Math.max(...lons);
    const minLat = Math.min(...lats),
        maxLat = Math.max(...lats);
    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;
    const lonSpan = Math.max(maxLon - minLon, 0.001);
    const latSpan = Math.max(maxLat - minLat, 0.001);

    const padding = 120;
    const availW = VIDEO_WIDTH - 2 * padding;
    const availH = VIDEO_HEIGHT - 2 * padding;
    // Mapbox GL uses 512 px tiles; one world = 512 px at zoom 0.
    const zoomLon = Math.log2((availW * 360) / (lonSpan * 512));
    const zoomLat = Math.log2(
        (availH * 360 * Math.cos((centerLat * Math.PI) / 180)) /
            (latSpan * 512),
    );
    const overviewZoom = Math.min(zoomLon, zoomLat);

    if (cameraMode === "follow") {
        return {
            center: [points[0].lon, points[0].lat],
            zoom: overviewZoom + 3,
            pitch: 60,
            bearing: 0,
        };
    }
    return {
        center: [centerLon, centerLat],
        zoom: overviewZoom - 0.75,
        pitch: 45,
        bearing: 0,
    };
}

const OutroStat: React.FC<{
    value: string;
    label: string;
    outroFrame: number;
    delay: number;
}> = ({ value, label, outroFrame, delay }) => {
    const opacity = interpolate(outroFrame, [delay, delay + 12], [0, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
    });
    const translateY = interpolate(outroFrame, [delay, delay + 12], [20, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
    });
    return (
        <div
            style={{
                textAlign: "center",
                marginBottom: 60,
                opacity,
                transform: `translateY(${translateY}px)`,
            }}
        >
            <div
                style={{
                    color: "#FFD042",
                    fontSize: 80,
                    fontWeight: 700,
                    fontFamily: "system-ui, sans-serif",
                }}
            >
                {value}
            </div>
            <div
                style={{
                    color: "#94a3b8",
                    fontSize: 44,
                    fontFamily: "system-ui, sans-serif",
                    marginTop: 8,
                }}
            >
                {label}
            </div>
        </div>
    );
};

export const MapScene: React.FC<Props> = ({
    tripData,
    mapboxToken,
    cameraMode,
    terrainEnabled,
    outroFrames,
    title,
    totalDistanceM,
    avgSpeedMps,
    distanceUnit = "nauticalMiles",
    speedUnit = "knots",
    underwayDurationSeconds,
    watermarkImage,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const boatMarkerRef = useRef<mapboxgl.Marker | null>(null);
    const boatMarkerDotRef = useRef<HTMLDivElement | null>(null);
    const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
    const [trackCamera, setTrackCamera] = useState<TrackCamera | null>(null);
    const [mapReady, setMapReady] = useState(false);
    const { delayRender, continueRender } = useDelayRender();
    const [handle] = useState(() => delayRender("mapbox-load"));
    const frame = useCurrentFrame();
    const { durationInFrames } = useVideoConfig();
    const { isRendering } = useRemotionEnvironment();

    // tripData.points is an array of underway segments (gaps between them
    // are moored time); the animation/camera logic below treats the whole
    // trip as one continuous path, so flatten once up front.
    const flatPoints = useMemo(
        (): TrackPoint[] => tripData.points.flat(),
        [tripData.points],
    );

    const smoothCoords = useMemo(
        (): [number, number][] => flatPoints.map((p) => [p.lon, p.lat]),
        [flatPoints],
    );

    // Map animation phase (excluding outro)
    const mapPhaseFrames = durationInFrames - (outroFrames ?? 0);
    // Outro phase
    const isOutroPhase = (outroFrames ?? 0) > 0 && frame >= mapPhaseFrames;
    const outroFrame = isOutroPhase ? frame - mapPhaseFrames : 0;
    const outroProgress =
        isOutroPhase && outroFrames && outroFrames > 1
            ? outroFrame / (outroFrames - 1)
            : 0;

    const cumulativeNm = useMemo(() => {
        const pts = flatPoints;
        const totals = [0];
        for (let i = 1; i < pts.length; i++) {
            const d = turfDistance(
                point([pts[i - 1].lon, pts[i - 1].lat]),
                point([pts[i].lon, pts[i].lat]),
                { units: "nauticalmiles" },
            );
            totals.push(totals[i - 1] + d);
        }
        return totals;
    }, [flatPoints]);

    // Cumulative underway seconds elapsed at each point in flatPoints — a raw
    // timestamp delta from the first point would include gaps between
    // underway segments (time spent moored), so this walks segment-by-
    // segment and only accumulates time within each one. Mirrors
    // BoatLocation.trackDuration on the Elm side (which underwayDurationSeconds
    // is derived from) exactly, so the two agree by the end of the track:
    // segments with fewer than 3 points don't count, and a segment's elapsed
    // time is measured from its second point, not its first.
    const cumulativeUnderwaySeconds = useMemo(() => {
        const totals: number[] = [];
        let base = 0;
        for (const segment of tripData.points) {
            if (segment.length < 3) {
                for (let i = 0; i < segment.length; i++) totals.push(base);
                continue;
            }
            const segmentStart = segment[1].timestamp;
            for (let i = 0; i < segment.length; i++) {
                const elapsed =
                    i === 0 ? 0 : (segment[i].timestamp - segmentStart) / 1000;
                totals.push(base + elapsed);
            }
            base +=
                (segment[segment.length - 1].timestamp - segmentStart) / 1000;
        }
        return totals;
    }, [tripData.points]);

    // Slows the track to a stop while each photo is held on screen, then
    // eases back up to speed once it's dismissed. photoCues' triggerFrames
    // are solved jointly with pausedFrameMap so each pause/fade-in lines up
    // with when the boat actually reaches that photo's location.
    const { photoCues, pausedFrameMap } = useMemo(
        () =>
            computePausedPhotoSchedule(
                tripData.photos,
                mapPhaseFrames,
                flatPoints,
                cumulativeNm,
            ),
        [tripData.photos, flatPoints, cumulativeNm, mapPhaseFrames],
    );

    // Track animation progress capped at end of map phase
    const trackProgress =
        mapPhaseFrames > 1
            ? Math.min(
                  1,
                  (pausedFrameMap[Math.min(frame, mapPhaseFrames - 1)] ?? 0) /
                      (mapPhaseFrames - 1),
              )
            : 1;

    // Fade-in leads the stop by PHOTO_LEAD_FRAMES (so the photo is already
    // visible once the boat comes to rest); fade-out mirrors that same
    // offset around the resume point, so the boat is already moving again
    // once the photo is fully gone instead of finishing early.
    const activePhotoCue = photoCues.find((cue) => {
        const showFrom = cue.triggerFrame - PHOTO_LEAD_FRAMES;
        const showUntil =
            cue.triggerFrame + PHOTO_DISPLAY_FRAMES + PHOTO_LEAD_FRAMES;
        return frame >= showFrom && frame < showUntil;
    });
    const photoOpacity = activePhotoCue
        ? interpolate(
              frame - (activePhotoCue.triggerFrame - PHOTO_LEAD_FRAMES),
              [
                  0,
                  PHOTO_FADE_FRAMES,
                  2 * PHOTO_LEAD_FRAMES +
                      PHOTO_DISPLAY_FRAMES -
                      PHOTO_FADE_FRAMES,
                  2 * PHOTO_LEAD_FRAMES + PHOTO_DISPLAY_FRAMES,
              ],
              [0, 1, 1, 0],
              { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
          )
        : 0;

    // Mild ease-in/out blend (40%) for a gentler start/stop
    const constantProgress = lerp(trackProgress, easeInOut(trackProgress), 0.4);

    const ptFrac = pointProgressFromDistance(cumulativeNm, constantProgress);

    const currentPoint = pointAtIndex(flatPoints, ptFrac);

    // Exponential smoothing of bearing in frame-space eliminates GPS jitter that
    // spatial window-averaging can't fix. alpha=0.04 → ~25-frame (0.83s) time constant.
    const followBearings = useMemo(() => {
        if (cameraMode !== "follow") return null;
        const alpha = 0.04;
        const result = new Float32Array(mapPhaseFrames);
        const n = flatPoints.length - 1;
        const indexWindow = Math.max(1, Math.ceil(n * 0.02));
        for (let f = 0; f < mapPhaseFrames; f++) {
            const p =
                mapPhaseFrames > 1
                    ? pausedFrameMap[f] / (mapPhaseFrames - 1)
                    : 1;
            const raw = smoothedBearingByIndex(
                flatPoints,
                pointProgressFromDistance(
                    cumulativeNm,
                    lerp(p, easeInOut(p), 0.4),
                ),
                indexWindow,
            );
            if (f === 0) {
                result[f] = raw;
            } else {
                let diff = raw - result[f - 1];
                while (diff > 180) diff -= 360;
                while (diff < -180) diff += 360;
                result[f] = result[f - 1] + alpha * diff;
            }
        }
        return result;
    }, [flatPoints, cumulativeNm, mapPhaseFrames, cameraMode, pausedFrameMap]);

    // Same exponential smoothing for camera position — rapid GPS back-and-forth
    // (e.g. swinging at a mooring) otherwise jerks the camera even though the
    // underlying track progress is barely moving. The boat marker itself still
    // tracks the raw (unsmoothed) position via currentPoint.
    const followCenters = useMemo(() => {
        if (cameraMode !== "follow") return null;
        const alpha = 0.04;
        const lons = new Float32Array(mapPhaseFrames);
        const lats = new Float32Array(mapPhaseFrames);
        for (let f = 0; f < mapPhaseFrames; f++) {
            const p =
                mapPhaseFrames > 1
                    ? pausedFrameMap[f] / (mapPhaseFrames - 1)
                    : 1;
            const raw = pointAtIndex(
                flatPoints,
                pointProgressFromDistance(
                    cumulativeNm,
                    lerp(p, easeInOut(p), 0.4),
                ),
            );
            if (f === 0) {
                lons[f] = raw.lon;
                lats[f] = raw.lat;
            } else {
                lons[f] = lons[f - 1] + alpha * (raw.lon - lons[f - 1]);
                lats[f] = lats[f - 1] + alpha * (raw.lat - lats[f - 1]);
            }
        }
        return { lons, lats };
    }, [flatPoints, cumulativeNm, mapPhaseFrames, cameraMode, pausedFrameMap]);

    // Mount map once
    useEffect(() => {
        if (!containerRef.current) return;

        mapboxgl.accessToken = mapboxToken;
        // Default is 16 concurrent raster tile/sprite/icon requests. At pitch
        // 60 a single frame can easily need more than that across the
        // satellite + DEM sources, so extra tiles queue in waves — each
        // ~300ms round trip, stacking into the clustered 300/580/800ms waits
        // seen per frame. Raising this lets them all fire in parallel.
        mapboxgl.maxParallelImageRequests = 64;

        // Mapbox fetches @2x raster tiles (4x the pixel data) whenever
        // devicePixelRatio >= 2. The final output is a fixed 1080x1920 that
        // gets compressed anyway, so retina tiles buy little — but at DPR 2
        // they roughly quadruple every tile fetch during preflight and
        // per-frame loading. Force @1x for export only; preview keeps the
        // user's real DPR.
        if (isRendering && typeof window !== "undefined") {
            Object.defineProperty(window, "devicePixelRatio", {
                value: 1,
                configurable: true,
            });
        }

        const initialPoint = flatPoints[0] ?? {
            lat: 0,
            lon: 0,
            heading: null,
        };
        const initCam = estimateInitialCamera(flatPoints, cameraMode);

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: satelliteStyle(terrainEnabled) as mapboxgl.StyleSpecification,
            center: initCam.center,
            zoom: initCam.zoom,
            pitch: initCam.pitch,
            bearing: initCam.bearing,
            interactive: false,
            preserveDrawingBuffer: true,
            antialias: true,
            attributionControl: false,
            // No cap — let Mapbox size the cache to what's actually in use.
            // The preflight sweep below deliberately visits dozens of camera
            // positions across several zoom levels to warm tiles ahead of
            // capture; a fixed cap here (previously 600) let early tiles get
            // evicted before rendering ever reached them, defeating the sweep.
            // Default 300ms label-collision fade keeps _placementDirty set
            // (and idle() from firing) for the whole fade on every camera
            // change — the label layers' equivalent of the raster-fade-
            // duration issue. Frames are captured discretely during export,
            // so there's no continuous motion for a fade to smooth anyway.
            fadeDuration: isRendering ? 0 : 300,
        });

        map.on("load", async () => {
            map.addSource("track-trail", {
                type: "geojson",
                data: buildTrail(smoothCoords, undefined, 0),
                tolerance: 0,
            });
            map.addLayer({
                id: "track-trail",
                type: "line",
                source: "track-trail",
                paint: {
                    "line-color": "#FFD042",
                    "line-width": 8,
                    "line-opacity": 0.9,
                },
                layout: { "line-cap": "round", "line-join": "round" },
            });

            // Mapbox hands this element straight to Marker as its root node,
            // and Marker runs its own recurring (~60ms, wall-clock) fog/terrain
            // occlusion pass that writes this._element.style.opacity directly
            // — see mapbox-gl's Marker._evaluateOpacity, which fires whenever
            // the style has fog or terrain configured (this one always has
            // fog). That fight over the same style property, on a timer
            // decoupled from Remotion's frame timing, is what caused the
            // outro flicker — a CSS transition wasn't the issue. Our fade
            // goes on an inner child div instead, so the two never touch the
            // same element.
            const markerEl = document.createElement("div");
            markerEl.style.cssText = "width:26px;height:26px;";
            const markerDot = document.createElement("div");
            markerDot.style.cssText =
                "width:100%;height:100%;border-radius:50%;background:#FFD042;" +
                "border:3px solid #ffffff;box-sizing:border-box;";
            markerEl.appendChild(markerDot);
            const boatMarker = new mapboxgl.Marker({
                element: markerEl,
                anchor: "center",
            })
                .setLngLat([initialPoint.lon, initialPoint.lat])
                .addTo(map);
            boatMarkerRef.current = boatMarker;
            boatMarkerDotRef.current = markerDot;

            // Photo pins are a native symbol layer (like the trail above)
            // rather than mapboxgl.Marker DOM overlays — @remotion/web-renderer
            // captures each frame either via the browser's experimental
            // drawElementImage or a DOM-composer fallback, and layering extra
            // HTML elements over the WebGL canvas triggered a Safari-only
            // "SyntaxError: The string did not match the expected pattern."
            // during export. Drawing pins on the canvas itself sidesteps that
            // DOM-capture path entirely. The pin icon is a camera glyph
            // rasterized from an SVG data URL and registered via addImage —
            // it must be loaded and registered before addLayer runs, so this
            // waits on the image's load event rather than assuming a data
            // URL decodes synchronously.
            const validPins = photoCues.filter(
                (cue) => Number.isFinite(cue.lon) && Number.isFinite(cue.lat),
            );
            map.addSource("photo-pins", {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: validPins.map((cue) => ({
                        type: "Feature",
                        properties: {},
                        geometry: {
                            type: "Point",
                            coordinates: [cue.lon, cue.lat],
                        },
                    })),
                },
            });

            const photoMarkerImage = await new Promise<HTMLImageElement>(
                (resolve, reject) => {
                    const img = new Image();
                    img.onload = () => resolve(img);
                    img.onerror = () =>
                        reject(new Error("Failed to load photo marker icon"));
                    img.src = PHOTO_MARKER_ICON_DATA_URL;
                },
            );
            if (!map.hasImage("photo-marker")) {
                map.addImage("photo-marker", photoMarkerImage);
            }
            map.addLayer({
                id: "photo-pins",
                type: "symbol",
                source: "photo-pins",
                layout: {
                    "icon-image": "photo-marker",
                    "icon-size": 48 / PHOTO_MARKER_ICON_SIZE,
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                },
            });

            const bounds = trackBounds(flatPoints);
            const cam = map.cameraForBounds(bounds, { padding: 120 });
            const camCenter = cam?.center
                ? mapboxgl.LngLat.convert(cam.center)
                : null;
            const derived: TrackCamera = {
                zoom: cam?.zoom ?? 12,
                center: camCenter
                    ? [camCenter.lng, camCenter.lat]
                    : [flatPoints[0].lon, flatPoints[0].lat],
            };
            activeTrackCamera = derived;
            setTrackCamera(derived);

            const jumpToFrame0 = () => {
                if (cameraMode === "follow") {
                    map.jumpTo({
                        center: [flatPoints[0].lon, flatPoints[0].lat],
                        zoom: derived.zoom + 3,
                        pitch: 60,
                        bearing:
                            followBearings?.[0] ??
                            smoothedBearing(flatPoints, 0),
                    });
                } else {
                    map.jumpTo({
                        center: derived.center,
                        zoom: derived.zoom - 0.75,
                        pitch: 45,
                        bearing: 0,
                    });
                }
                activeMapInstance = map;
                setMapInstance(map);
                map.once("idle", () => {
                    setMapReady(true);
                    continueRender(handle);
                });
            };

            if (!isRendering) {
                // Preview: show the map immediately — tiles load progressively.
                // The container has no visibility gate in preview so this is
                // visible as soon as the style finishes loading.
                if (cameraMode === "follow") {
                    map.jumpTo({
                        center: [flatPoints[0].lon, flatPoints[0].lat],
                        zoom: derived.zoom + 3,
                        pitch: 60,
                        bearing:
                            followBearings?.[0] ??
                            smoothedBearing(flatPoints, 0),
                    });
                } else {
                    map.jumpTo({
                        center: derived.center,
                        zoom: derived.zoom - 0.75,
                        pitch: 45,
                        bearing: 0,
                    });
                }
                activeMapInstance = map;
                setMapInstance(map);
                setMapReady(true);
                continueRender(handle);
            } else {
                // During export, sweep the full route to warm the tile cache while
                // Remotion is blocked on delayRender. setMapInstance is deferred
                // until after the sweep so the per-frame effect can't conflict.
                const STEPS = cameraMode === "follow" ? 24 : 6;
                let step = 0;
                // Same window formula as the followBearings smoothing memo,
                // for a comparably smoothed warm-sweep bearing.
                const warmIndexWindow = Math.max(
                    1,
                    Math.ceil((flatPoints.length - 1) * 0.02),
                );

                // Follow mode's outro zooms the camera out from the close-in
                // follow zoom (derived.zoom + 3) to the full-track overview
                // zoom (derived.zoom - 0.75) — see the isOutroPhase branch
                // below. The route sweep above only ever visits zoom + 3, so
                // every intermediate zoom level crossed during that zoom-out
                // was never cached and got fetched live, which is exactly
                // the tile pop-in visible on export during the final
                // zoom-out. Walk the same camera path the real outro uses so
                // those tiles are already cached by the time it renders.
                // (Overview mode's outro only changes pitch/bearing, not
                // center or zoom, so it's already fully covered by the sweep
                // above and needs no extra warming.)
                const OUTRO_WARM_STEPS = 10;
                let outroWarmStep = 0;
                const endPoint = pointAtProgress(flatPoints, 1);
                const endBearing = smoothedBearing(flatPoints, 1);
                const visitOutroZoom = () => {
                    if (outroWarmStep > OUTRO_WARM_STEPS) {
                        jumpToFrame0();
                        return;
                    }
                    const t = outroWarmStep / OUTRO_WARM_STEPS;
                    map.jumpTo({
                        center: [
                            lerp(endPoint.lon, derived.center[0], t),
                            lerp(endPoint.lat, derived.center[1], t),
                        ],
                        zoom: lerp(derived.zoom + 3, derived.zoom - 0.75, t),
                        pitch: lerp(60, 20, t),
                        bearing: lerpAngle(endBearing, 0, t),
                    });
                    outroWarmStep++;
                    map.once("idle", visitOutroZoom);
                };

                const visitNext = () => {
                    if (step > STEPS) {
                        if (cameraMode === "follow") {
                            visitOutroZoom();
                        } else {
                            jumpToFrame0();
                        }
                        return;
                    }

                    const p = step / STEPS;
                    if (cameraMode === "follow") {
                        // Distance-based, matching the per-frame camera position
                        // during actual rendering (pointProgressFromDistance),
                        // not pointAtProgress's time-based interpolation — those
                        // two diverge whenever speed varies along the track (e.g.
                        // sitting at a mooring), leaving real playback positions
                        // this sweep never visited.
                        const idxFrac = pointProgressFromDistance(
                            cumulativeNm,
                            p,
                        );
                        const pt = pointAtIndex(flatPoints, idxFrac);
                        map.jumpTo({
                            center: [pt.lon, pt.lat],
                            zoom: derived.zoom + 3,
                            pitch: 60,
                            bearing: smoothedBearingByIndex(
                                flatPoints,
                                idxFrac,
                                warmIndexWindow,
                            ),
                        });
                    } else {
                        map.jumpTo({
                            center: derived.center,
                            zoom: derived.zoom - 0.75,
                            pitch: 45,
                            bearing: p * 360,
                        });
                    }
                    step++;
                    map.once("idle", visitNext);
                };

                visitNext();
            }
        });

        return () => {
            map.remove();
            boatMarkerRef.current?.remove();
            boatMarkerRef.current = null;
            boatMarkerDotRef.current = null;
            setMapInstance(null);
            if (activeMapInstance === map) {
                activeMapInstance = null;
                activeTrackCamera = null;
            }
        };
    }, []);

    // The map is only constructed once (empty deps above), so the style's
    // baked-in terrain setting reflects whatever terrainEnabled was at mount
    // time. Toggling the config switch while the preview is already mounted
    // needs this separate live update to actually change what's on screen.
    useEffect(() => {
        if (!mapInstance) return;
        mapInstance.setTerrain(terrainEnabled ? TERRAIN_SPEC : null);
    }, [mapInstance, terrainEnabled]);

    // Update camera and trail each frame; pause Remotion rendering until tiles settle.
    // Deliberately useLayoutEffect, not useEffect: @remotion/web-renderer updates
    // the frame via flushSync and then synchronously polls a readiness flag. A
    // plain useEffect here is a passive effect deferred past that poll, so the
    // delayRender("mapbox-tiles") below could register too late — after the
    // renderer already decided the (stale) frame was ready to capture. Layout
    // effects run inside the same flushSync commit, closing that race.
    useLayoutEffect(() => {
        if (!mapInstance || !trackCamera) return;

        (
            mapInstance.getSource("track-trail") as
                mapboxgl.GeoJSONSource | undefined
        )?.setData(buildTrail(smoothCoords, currentPoint, ptFrac));
        if (boatMarkerRef.current) {
            boatMarkerRef.current.setLngLat([
                currentPoint.lon,
                currentPoint.lat,
            ]);
            const boatOpacity = isOutroPhase
                ? Math.max(0, 1 - outroProgress * 4)
                : hudOpacity;
            if (boatMarkerDotRef.current) {
                boatMarkerDotRef.current.style.opacity = String(boatOpacity);
            }
        }

        if (isOutroPhase) {
            // Zoom out from end-of-track follow position to full track overview
            if (cameraMode === "follow") {
                const endPoint = pointAtProgress(flatPoints, 1);
                const endBearing = smoothedBearing(flatPoints, 1);
                const t = easeInOut(Math.min(1, outroProgress / 0.65));
                mapInstance.jumpTo({
                    center: [
                        lerp(endPoint.lon, trackCamera.center[0], t),
                        lerp(endPoint.lat, trackCamera.center[1], t),
                    ],
                    zoom: lerp(
                        trackCamera.zoom + 3,
                        trackCamera.zoom - 0.75,
                        t,
                    ),
                    pitch: lerp(60, 20, t),
                    bearing: lerpAngle(endBearing, 0, t),
                });
            } else {
                const endBearing =
                    ((mapPhaseFrames - 1) / durationInFrames) * 360;
                const t = easeInOut(Math.min(1, outroProgress / 0.65));
                mapInstance.jumpTo({
                    center: trackCamera.center,
                    zoom: trackCamera.zoom - 0.75,
                    pitch: lerp(45, 20, t),
                    bearing: lerpAngle(endBearing, 0, t),
                });
            }
        } else if (cameraMode === "follow") {
            mapInstance.jumpTo({
                center: followCenters
                    ? [followCenters.lons[frame], followCenters.lats[frame]]
                    : [currentPoint.lon, currentPoint.lat],
                zoom: trackCamera.zoom + 3,
                pitch: 60,
                bearing:
                    followBearings?.[frame] ??
                    smoothedBearing(flatPoints, trackProgress),
            });
        } else {
            const bearing = (frame / durationInFrames) * 360;
            // Zoom out slightly from the flat-view bounds to give headroom for
            // pitch=45° compression and any bearing angle.
            const zoom = trackCamera.zoom - 0.75;
            mapInstance.jumpTo({
                center: trackCamera.center,
                zoom,
                pitch: 45,
                bearing,
            });
        }

        // Export: wait for idle so every captured frame has fully-loaded tiles
        // (the preflight pre-warms the cache, so idle is usually fast). A
        // single "idle" can still fire before an async GeoJSON setData (the
        // trail update above) has finished its worker round-trip and reached
        // the GPU, letting the WebGL trail visibly lag the DOM-based boat
        // marker by a frame or more — so keep re-listening until the source
        // has actually settled, not just on the first idle event.
        // Preview: wait only for the next render event — tiles load progressively
        // and blocking on idle would stall scrubbing.
        const tileHandle = delayRender("mapbox-tiles");
        let resolved = false;
        const resolve = () => {
            if (resolved) return;
            resolved = true;
            continueRender(tileHandle);
        };

        const event = isRendering ? "idle" : "render";
        const checkSettled = () => {
            if (
                !isRendering ||
                (mapInstance.isSourceLoaded("track-trail") &&
                    mapInstance.loaded())
            ) {
                resolve();
            } else {
                mapInstance.once(event, checkSettled);
            }
        };
        mapInstance.once(event, checkSettled);
        mapInstance.triggerRepaint();

        return () => {
            mapInstance.off(event, checkSettled);
            resolve();
        };
    }, [frame, mapInstance, trackCamera]);

    const idx = Math.max(
        0,
        Math.min(flatPoints.length - 2, Math.floor(ptFrac)),
    );
    const frac = ptFrac - idx;
    const currentDistanceNm =
        (cumulativeNm[idx] ?? 0) +
        frac * ((cumulativeNm[idx + 1] ?? 0) - (cumulativeNm[idx] ?? 0));
    const currentUnderwaySeconds =
        (cumulativeUnderwaySeconds[idx] ?? 0) +
        frac *
            ((cumulativeUnderwaySeconds[idx + 1] ?? 0) -
                (cumulativeUnderwaySeconds[idx] ?? 0));

    const localTime = new Date(currentPoint.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    // HUD fades in at start of track, fades out when outro begins
    const hudOpacity = isOutroPhase
        ? interpolate(outroProgress, [0, 0.2], [1, 0], {
              extrapolateRight: "clamp",
          })
        : interpolate(trackProgress, [0, 0.05], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
          });

    // Scrim and content start after zoom-out is mostly done (~60% through outro)
    const scrimOpacity = interpolate(outroProgress, [0.55, 0.8], [0, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
    });
    const headerOpacity = interpolate(outroFrame, [52, 67], [0, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
    });
    const headerY = interpolate(outroFrame, [52, 67], [30, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
    });

    return (
        <div
            style={{
                width: VIDEO_WIDTH,
                height: VIDEO_HEIGHT,
                position: "relative",
                overflow: "hidden",
            }}
        >
            <figure
                ref={containerRef}
                style={{
                    width: "100%",
                    height: "100%",
                    visibility: mapReady ? undefined : "hidden",
                }}
            />

            {/* Photo cue — briefly covers the map when its timestamp is reached; the
                track keeps animating underneath. */}
            {activePhotoCue && (
                <img
                    src={activePhotoCue.dataUrl}
                    crossOrigin="anonymous"
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: photoOpacity,
                    }}
                />
            )}

            {/* HUD — rendered on transparent background, composited on top of map in export */}
            <div
                style={{
                    position: "absolute",
                    bottom: 90,
                    left: 32,
                    right: 32,
                    display: "flex",
                    gap: 16,
                    opacity: hudOpacity,
                }}
            >
                <HudBadge value={localTime} color="#FFD042" />
                <HudBadge
                    value={formatDuration(currentUnderwaySeconds)}
                    color="#FFD042"
                />
                <HudBadge
                    value={formatDistance(
                        currentDistanceNm * 1852,
                        distanceUnit,
                    )}
                    color="#FFD042"
                />
            </div>

            {/* Nebo.fleet branding — fades out alongside the watermark logo as outro begins */}
            <div
                style={{
                    position: "absolute",
                    bottom: 32,
                    right: 32,
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 8,
                    opacity: isOutroPhase
                        ? interpolate(outroProgress, [0, 0.4], [1.0, 0], {
                              extrapolateRight: "clamp",
                          })
                        : 1.0,
                }}
            >
                <span
                    style={{
                        color: "#ffffff",
                        fontSize: 22,
                        fontFamily: "system-ui, sans-serif",
                        lineHeight: 1,
                    }}
                >
                    Powered by
                </span>
                <img
                    src="/images/nebofleet.svg"
                    crossOrigin="anonymous"
                    style={{
                        // Explicit size (270x48 source ratio) avoids "auto" width +
                        // max-constraints sizing, which rendered differently on export.
                        width: 160,
                        height: (160 * 48) / 270,
                        objectFit: "contain",
                    }}
                />
            </div>

            {/* Outro — stats overlay with dark scrim over the zoomed-out map */}
            {isOutroPhase && (
                <>
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0, 5, 20, 0.65)",
                            opacity: scrimOpacity,
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {watermarkImage && (
                            <img
                                src={watermarkImage}
                                crossOrigin="anonymous"
                                style={{
                                    maxHeight: 220,
                                    maxWidth: 420,
                                    width: "auto",
                                    objectFit: "contain",
                                    marginBottom: 80,
                                    opacity: headerOpacity,
                                    transform: `translateY(${headerY}px)`,
                                }}
                            />
                        )}
                        {title && (
                            <div
                                style={{
                                    color: "#ffffff",
                                    fontSize: 80,
                                    fontWeight: 700,
                                    fontFamily: "system-ui, sans-serif",
                                    marginBottom: 80,
                                    opacity: headerOpacity,
                                    transform: `translateY(${headerY}px)`,
                                }}
                            >
                                {title}
                            </div>
                        )}
                        {totalDistanceM != null && (
                            <OutroStat
                                value={formatDistance(
                                    totalDistanceM,
                                    distanceUnit,
                                )}
                                label="Distance"
                                outroFrame={outroFrame}
                                delay={58}
                            />
                        )}
                        {avgSpeedMps != null && (
                            <OutroStat
                                value={formatSpeed(avgSpeedMps, speedUnit)}
                                label="Avg Speed"
                                outroFrame={outroFrame}
                                delay={66}
                            />
                        )}
                        {underwayDurationSeconds != null && (
                            <OutroStat
                                value={formatDuration(underwayDurationSeconds)}
                                label="Duration"
                                outroFrame={outroFrame}
                                delay={74}
                            />
                        )}
                    </div>
                </>
            )}

            {/* Watermark logo — fades out as outro begins to avoid duplication with the outro scene */}
            {watermarkImage && (
                <img
                    src={watermarkImage}
                    crossOrigin="anonymous"
                    style={{
                        position: "absolute",
                        top: 32,
                        left: 32,
                        maxHeight: 160,
                        maxWidth: 280,
                        width: "auto",
                        objectFit: "contain",
                        opacity: isOutroPhase
                            ? interpolate(outroProgress, [0, 0.4], [1.0, 0], {
                                  extrapolateRight: "clamp",
                              })
                            : 1.0,
                    }}
                />
            )}
        </div>
    );
};

const HudBadge: React.FC<{ value: string; color: string }> = ({
    value,
    color,
}) => (
    <div
        style={{
            flex: 1,
            background: "rgba(0,0,0,0.55)",
            borderRadius: 16,
            height: 110,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            fontSize: 52,
            fontWeight: 700,
            fontFamily: "system-ui, sans-serif",
        }}
    >
        {value}
    </div>
);

function buildTrail(
    coords: [number, number][],
    tip: TrackPoint | undefined,
    ptFrac: number,
): GeoJSON.Feature<GeoJSON.LineString> {
    const start = coords[0] ?? [0, 0];
    if (!tip || coords.length < 2) {
        return {
            type: "Feature",
            properties: {},
            geometry: { type: "LineString", coordinates: [start, start] },
        };
    }
    const segIndex = Math.min(Math.floor(ptFrac) + 1, coords.length - 1);
    const trailCoords: [number, number][] = [
        ...coords.slice(0, segIndex),
        [tip.lon, tip.lat],
    ];
    return {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: trailCoords },
    };
}

function trackBounds(points: TrackPoint[]): mapboxgl.LngLatBoundsLike {
    const lons = points.map((p) => p.lon);
    const lats = points.map((p) => p.lat);
    return [
        [Math.min(...lons), Math.min(...lats)],
        [Math.max(...lons), Math.max(...lats)],
    ];
}
